import type { PublicAssessment } from '@really-assess/shared/assessments'

const API_BASE = ''

export async function fetchPublicAssessment(slug: string): Promise<PublicAssessment> {
  const response = await fetch(`${API_BASE}/api/assessments/${encodeURIComponent(slug)}`, {
    credentials: 'include',
  })
  if (!response.ok) {
    throw new Error('assessment_not_found')
  }
  const data = await response.json() as { assessment: PublicAssessment }
  return data.assessment
}

export interface AdminAssessment extends PublicAssessment {
  webhookUrl: string
  webhookSecret: string
  createdAt: string
  updatedAt: string
}

export async function adminLogin(email: string, password: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  })
  if (!response.ok) throw new Error('invalid_credentials')
}

export async function adminLogout(): Promise<void> {
  await fetch(`${API_BASE}/api/admin/logout`, {
    method: 'POST',
    credentials: 'include',
  })
}

export async function fetchAdminMe(): Promise<{ email: string } | null> {
  const response = await fetch(`${API_BASE}/api/admin/me`, { credentials: 'include' })
  if (!response.ok) return null
  const data = await response.json() as { admin: { email: string } }
  return data.admin
}

export async function listAdminAssessments(): Promise<AdminAssessment[]> {
  const response = await fetch(`${API_BASE}/api/admin/assessments`, { credentials: 'include' })
  if (!response.ok) throw new Error('unauthorized')
  const data = await response.json() as { assessments: AdminAssessment[] }
  return data.assessments
}

export async function createAdminAssessment(payload: Record<string, unknown>): Promise<AdminAssessment> {
  const response = await fetch(`${API_BASE}/api/admin/assessments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ?? 'create_failed')
  }
  const data = await response.json() as { assessment: AdminAssessment }
  return data.assessment
}

export async function updateAdminAssessment(
  slug: string,
  payload: Record<string, unknown>
): Promise<AdminAssessment> {
  const response = await fetch(`${API_BASE}/api/admin/assessments/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ?? 'update_failed')
  }
  const data = await response.json() as { assessment: AdminAssessment }
  return data.assessment
}

export async function archiveAdminAssessment(slug: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/admin/assessments/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!response.ok) throw new Error('archive_failed')
}
